import { ReactNode } from 'react'
import { Button } from '@/components/Button'
import { ListItem } from '@/components/ListItem'
import { NumberField } from '@/components/NumberField'
import { Calendar } from '@/components/Calendar'
import { DateField } from '@/components/DateField'
import { usePopoverTitleId } from '@/components/Popover'
import { useRovingFocus } from '@/hooks/useRovingFocus'
import { getMaxDaysOut } from '@/data/config'
import { toLocalIsoDate } from '@/utils/formatDate'

/* ---------- Shared types ---------- */

export interface FilterOption {
	value: string
	label: string
}

// A scroll container clips on both axes; the 4px gutter fits ListItem's ring + offset, and the negative margin cancels it.
const SCROLL_LIST_CLASSES = 'spaces:overflow-y-auto spaces:-mx-4 spaces:-mt-4 spaces:px-4 spaces:pt-4'

/* ---------- Content: Multi-Select ---------- */

export const MULTI_SELECT_MIN_WIDTH = 304

interface MultiSelectContentProps {
	options: FilterOption[]
	selected: string[]
	onChange: (selected: string[]) => void
	selectionIndicator?: 'checkbox' | 'check'
	maxHeight?: number | null
	ariaLabel?: string
}

export function MultiSelectContent ({
	options,
	selected,
	onChange,
	selectionIndicator = 'checkbox',
	maxHeight = 384,
	ariaLabel
}: MultiSelectContentProps) {
	const { listRef, onKeyDown, getItemProps } = useRovingFocus(
		options.map(o => o.value),
		options.find(o => selected.includes(o.value))?.value
	)

	const toggle = (value: string) => {
		onChange(
			selected.includes(value)
				? selected.filter(v => v !== value)
				: [...selected, value]
		)
	}

	return (
		<div
			ref={listRef}
			role='listbox'
			aria-multiselectable='true'
			aria-label={ariaLabel}
			onKeyDown={onKeyDown}
			className={`spaces-filter-content-multi spaces:flex spaces:flex-col spaces:pb-8${
				maxHeight != null ? ` ${SCROLL_LIST_CLASSES}` : ''
			}`}
			style={maxHeight != null ? { maxHeight } : undefined}
		>
			{options.map(opt => (
				<ListItem
					key={opt.value}
					label={opt.label}
					selectionIndicator={selectionIndicator}
					isSelected={selected.includes(opt.value)}
					onToggle={() => toggle(opt.value)}
					{...getItemProps(opt.value)}
				/>
			))}
		</div>
	)
}

/* ---------- Content: Single-Select ---------- */

export const SINGLE_SELECT_MIN_WIDTH = 304

interface SingleSelectContentProps {
	options: FilterOption[]
	selected: string | null
	onChange: (value: string | null) => void
	maxHeight?: number | null
	ariaLabel?: string
}

export function SingleSelectContent ({
	options,
	selected,
	onChange,
	maxHeight = 384,
	ariaLabel
}: SingleSelectContentProps) {
	const { listRef, onKeyDown, getItemProps } = useRovingFocus(
		options.map(o => o.value),
		selected
	)

	return (
		<div
			ref={listRef}
			role='listbox'
			aria-label={ariaLabel}
			onKeyDown={onKeyDown}
			className={`spaces-filter-content-single spaces:flex spaces:flex-col spaces:pb-8${
				maxHeight != null ? ` ${SCROLL_LIST_CLASSES}` : ''
			}`}
			style={maxHeight != null ? { maxHeight } : undefined}
		>
			{options.map(opt => (
				<ListItem
					key={opt.value}
					label={opt.label}
					selectionIndicator='check'
					isSelected={opt.value === selected}
					onToggle={() => onChange(opt.value === selected ? null : opt.value)}
					{...getItemProps(opt.value)}
				/>
			))}
		</div>
	)
}

/* ---------- Content: Range ---------- */

export const RANGE_MIN_WIDTH = 342

interface RangeContentProps {
	min: number
	max: number
	valueMin: number | undefined
	valueMax: number | undefined
	onMinChange: (value: number | undefined) => void
	onMaxChange: (value: number | undefined) => void
	step?: number
	prefix?: string
	labelMin?: string
	labelMax?: string
}

export function RangeContent ({
	min,
	max,
	valueMin,
	valueMax,
	onMinChange,
	onMaxChange,
	step = 1,
	prefix,
	labelMin = 'Min',
	labelMax = 'Max'
}: RangeContentProps) {
	return (
		<div className='spaces-filter-content-range spaces:flex spaces:flex-col spaces:pb-16 spaces:px-8 spaces:pt-8 spaces:gap-16'>
			<div className='spaces:flex spaces:items-center spaces:gap-16'>
				<NumberField
					value={valueMin}
					onValueChange={onMinChange}
					onClear={() => onMinChange(undefined)}
					min={min}
					max={valueMax ?? max}
					step={step}
					prefix={prefix}
					label={labelMin}
					placeholder='None'
					className='spaces:flex-1'
				/>
				<NumberField
					value={valueMax}
					onValueChange={onMaxChange}
					onClear={() => onMaxChange(undefined)}
					min={valueMin ?? min}
					max={max}
					step={step}
					prefix={prefix}
					label={labelMax}
					placeholder='None'
					className='spaces:flex-1'
				/>
			</div>
		</div>
	)
}

/* ---------- Content: Date ---------- */

export const DATE_MIN_WIDTH = 312

function defaultMaxDate (): string {
	const d = new Date()
	d.setDate(d.getDate() + getMaxDaysOut())
	return toLocalIsoDate(d)
}

interface DateContentProps {
	value: string
	onValueChange: (value: string) => void
	label?: string
	calendarMonth: number
	calendarYear: number
	onCalendarMonthChange: (month: number, year: number) => void
	minDate?: string
	maxDate?: string
}

export function DateContent ({
	value,
	onValueChange,
	label = 'Move-In Date',
	calendarMonth,
	calendarYear,
	onCalendarMonthChange,
	minDate = toLocalIsoDate(new Date()),
	maxDate = defaultMaxDate()
}: DateContentProps) {
	const handleDateFieldChange = (v: string) => {
		onValueChange(v)
		const parsed = new Date(v + 'T00:00:00')
		if (!isNaN(parsed.getTime())) {
			onCalendarMonthChange(parsed.getMonth(), parsed.getFullYear())
		}
	}

	return (
		<div className='spaces-filter-content-date spaces:flex spaces:flex-col spaces:pb-16 spaces:px-8 spaces:pt-8 spaces:gap-16'>
			<Calendar
				value={value}
				onValueChange={onValueChange}
				month={calendarMonth}
				year={calendarYear}
				onMonthChange={onCalendarMonthChange}
				minDate={minDate}
				maxDate={maxDate}
			/>
			<DateField
				value={value}
				onValueChange={handleDateFieldChange}
				label={label}
				hideLabel
				minDate={minDate}
				maxDate={maxDate}
			/>
		</div>
	)
}

/* ---------- FilterContent Shell ---------- */

interface FilterContentProps {
	title: string
	children: ReactNode
	onClear?: () => void
	onCancel?: () => void
	onSave?: () => void
}

export function FilterContent ({
	title,
	children,
	onClear,
	onCancel,
	onSave
}: FilterContentProps) {
	const titleId = usePopoverTitleId()

	return (
		<div className='spaces-filter-content'>
			{/* Content */}
			<div className='spaces-filter-content-body spaces:pt-8 spaces:px-8'>
				<div className='spaces:p-8'>
					<h2 id={titleId} className='spaces:text-16/24 spaces:font-strong spaces:text-content-1'>
						{title}
					</h2>
				</div>
				<div>{children}</div>
			</div>
			{/* Actions */}
			<div className='spaces-filter-content-actions spaces:flex spaces:items-center spaces:justify-between spaces:pl-8 spaces:pr-16 spaces:py-16 spaces:border-t spaces:border-canvas-3'>
				<Button
					label='Clear'
					appearance='ghost'
					color='content3'
					size='md'
					spacesClass='spaces-clear'
					onClick={onClear}
				/>
				<div className='spaces:flex spaces:items-center spaces:gap-8'>
					<Button
						label='Cancel'
						appearance='stroke'
						color='content2'
						size='md'
						spacesClass='spaces-cancel'
						onClick={onCancel}
					/>
					<Button
						label='Save'
						appearance='solid'
						color='brand'
						size='md'
						spacesClass='spaces-apply'
						onClick={onSave}
					/>
				</div>
			</div>
		</div>
	)
}
